import type { ISingleTask } from "@/types/(company)/tasks.interface";

/** Matches TASK_CONSTANTS.POSITION_STEP on the API. */
const POSITION_STEP = 1000;

/**
 * Mirror the server's move semantics on a cached task list. The API treats
 * `position` as an insert index among the destination column's siblings
 * (dragged task excluded) and re-sequences the column in POSITION_STEP gaps.
 * Cached `position` values are those rank numbers — writing the raw index
 * into the field would sort the card to the top of the column — so translate
 * the index into a rank between its new neighbours instead.
 */
export const applyOptimisticMove = (
  tasks: ISingleTask[] | undefined,
  taskId: number,
  payload: { columnId: number; position: number },
): void => {
  const task = tasks?.find((t) => t.id === taskId);
  if (!tasks || !task) return;
  const siblings = tasks
    .filter((t) => t.columnId === payload.columnId && t.id !== taskId)
    .sort((a, b) => a.position - b.position);
  const at = Math.max(0, Math.min(payload.position, siblings.length));
  const prev = siblings[at - 1];
  const next = siblings[at];
  task.columnId = payload.columnId;
  task.position =
    prev && next
      ? (prev.position + next.position) / 2
      : prev
        ? prev.position + POSITION_STEP
        : next
          ? next.position - POSITION_STEP
          : 0;
};

/**
 * Mirror the server's backlog-reorder semantics on a cached task list so the
 * drag settles instantly instead of snapping back for a refetch. The API
 * reindexes every id in `taskIds` (in order) to `i * POSITION_STEP` and moves
 * them all into the target bucket (`sprintId`, null = backlog) — the frontend
 * sends the whole destination bucket, so this reproduces that exactly.
 */
export const applyOptimisticBacklogReorder = (
  tasks: ISingleTask[] | undefined,
  payload: { taskIds: number[]; sprintId?: number | null },
): void => {
  if (!tasks) return;
  const sprintId = payload.sprintId ?? null;
  payload.taskIds.forEach((id, i) => {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;
    task.backlogRank = i * POSITION_STEP;
    task.sprintId = sprintId;
  });
};
